// Semiconductores para protoboard en 3D: transistores TO-92, MOSFET TO-220, diodo DO-41 y 74HC595 (DIP-16).
import * as THREE from 'three';
import { Builder, M, mat, text } from '../kit.js';
import { getPins } from '../../parts/registry.js';
import { P, labelPlane, sharedGeo, sharedTexture } from './actuators-common.js';

// Transistor TO-92 ---------------------------------------------------------------------------------------
function to92Geo() {
  return sharedGeo('to92', () => {
    // Cara plana al frente (z = +1.2) y lomo redondo atrás; la forma va en (x, −z).
    const a = Math.asin(1.4 / 2.4);
    const s = new THREE.Shape();
    s.moveTo(2.4 * Math.cos(a), -1.2);
    s.absarc(0, 0.2, 2.4, -a, Math.PI + a, false);
    s.lineTo(2.4 * Math.cos(a), -1.2);
    const g = new THREE.ExtrudeGeometry(s, { depth: 4.8, bevelEnabled: false, curveSegments: 24 });
    g.rotateX(-Math.PI / 2);
    return g;
  });
}

function buildTo92(lines) {
  return () => {
    const b = new Builder();
    const y0 = 3.4;
    b.add(to92Geo(), M.blackPlastic, { y: y0 });
    // Patas: E y C se abren a 2.54 mm desde el paso de 1.27 mm del encapsulado
    for (const i of [-1, 0, 1]) {
      const top = [i * 1.27, y0 + 0.2, 0.1];
      b.leg(i ? [top, [i * 1.27, y0 - 1.0, 0.1], [i * P, 1.3, 0], [i * P, 0, 0]] : [top, [0, 0, 0]], 0.22, M.tin);
    }
    b.build();
    const label = labelPlane(3.8, 4.0, `to92-${lines.join('-')}`, (g, w, d) => {
      text(g, lines[0], w / 2, 1.3, { size: 0.95, color: '#c9cbcf', weight: 700 });
      text(g, lines[1], w / 2, 2.55, { size: 0.95, color: '#c9cbcf', weight: 700 });
      text(g, lines[2], w / 2, 3.6, { size: 0.7, color: '#a5a7ab', weight: 600 });
    }, { s: 40 });
    label.position.set(0, y0 + 2.6, 1.21);
    b.root.add(label);
    return { root: b.root };
  };
}

// MOSFET TO-220 --------------------------------------------------------------------------------------------
export function buildMosfet() {
  const b = new Builder();
  const metal = mat('#c7cace', { roughness: 0.3, metalness: 0.9 });
  const tab = sharedGeo('to220-tab', () => {
    const s = new THREE.Shape();
    s.moveTo(-5, 4.4);
    s.lineTo(5, 4.4);
    s.lineTo(5, 19.6);
    s.lineTo(-5, 19.6);
    s.closePath();
    const h = new THREE.Path();
    h.absarc(0, 16.4, 1.8, 0, Math.PI * 2, true);
    s.holes.push(h);
    return new THREE.ExtrudeGeometry(s, { depth: 1.3, bevelEnabled: false, curveSegments: 20 });
  });
  b.add(tab, metal, { z: -2.0 });
  b.box(10, 9.0, 3.2, M.blackPlastic, 0, 4.4, 0.9);
  for (const i of [-1, 0, 1]) {
    b.box(1.4, 1.5, 0.5, M.tin, i * P, 2.9, 0);
    b.box(0.75, 2.95, 0.45, M.tin, i * P, 0, 0);
  }
  b.build();
  const label = labelPlane(9.2, 7.4, 'to220-irlz44n', (g, w, d) => {
    text(g, 'IR', 1.8, 1.6, { size: 1.5, color: '#c3c5c9', weight: 800 });
    text(g, 'IRLZ44N', w / 2, 3.6, { size: 1.6, color: '#c9cbcf', weight: 700 });
    text(g, 'P 249X', w / 2, 5.6, { size: 1.1, color: '#a5a7ab', weight: 600 });
  }, { s: 30 });
  label.position.set(0, 8.9, 2.51);
  b.root.add(label);
  return { root: b.root };
}

// Diodo DO-41 ---------------------------------------------------------------------------------------------
function diodeTexture() {
  // Cilindro acostado sobre X: v = 0 en +X (cátodo), u = 0.25 hacia arriba.
  return sharedTexture('do41', 64, 52, (g) => {
    g.fillStyle = '#18181a';
    g.fillRect(0, 0, 64, 52);
    g.fillStyle = '#b8bcc2';
    g.fillRect(0, 38, 64, 7);
    g.save();
    g.translate(16, 20);
    g.rotate(-Math.PI / 2);
    text(g, '1N4007', 0, 0, { size: 7.5, color: '#b3b6bb', weight: 700 });
    g.restore();
  }, 8);
}

export function buildDiode() {
  const b = new Builder();
  const y = 2.4;
  for (const s of [-1, 1]) b.leg([[s * 2.4, y, 0], [s * 3.6, y, 0], [s * 2 * P, y - 0.9, 0], [s * 2 * P, 0, 0]], 0.26, M.tin);
  b.build();
  const side = new THREE.MeshStandardMaterial({ map: diodeTexture(), roughness: 0.4 });
  const cap = mat('#18181a', { roughness: 0.45 });
  const body = new THREE.Mesh(sharedGeo('do41', () => new THREE.CylinderGeometry(1.35, 1.35, 5.2, 24)), [side, cap, cap]);
  body.rotation.z = Math.PI / 2;
  body.position.y = y;
  body.castShadow = true;
  b.root.add(body);
  return { root: b.root };
}

// Chips DIP-16 para protoboard (74HC595, 74HC165, L293D) -----------------------------------------------
function buildDip16(key, line1, line2) {
  return (def, props) => {
    const b = new Builder();
    const pins = getPins(def.type, props);
    const len = 8 * P - 0.6;
    b.box(len, 3.3, 6.35, M.chip, 0, 0.9, 0);
    for (const p of pins) {
      const s = Math.sign(p.z);
      b.box(0.55, 0.28, 0.9, M.tin, p.x, 1.85, s * 3.4);
      b.box(1.1, 1.0, 0.28, M.tin, p.x, 1.0, s * 3.81);
      b.box(0.5, 1.2, 0.26, M.tin, p.x, 0, s * 3.81);
    }
    b.build();
    const top = labelPlane(len, 6.35, `dip16-${key}`, (g, w, d) => {
      g.fillStyle = '#1b1b1e';
      g.fillRect(0, 0, w, d);
      g.fillStyle = '#0f0f11';
      g.beginPath();
      g.arc(0, d / 2, 1.0, -Math.PI / 2, Math.PI / 2);
      g.fill();
      g.beginPath();
      g.arc(1.6, d - 1.3, 0.45, 0, Math.PI * 2);
      g.fill();
      text(g, line1, w / 2 + 0.8, d / 2 - 0.7, { size: 1.45, color: '#acaeb2', weight: 600 });
      text(g, line2, w / 2 + 0.8, d / 2 + 1.25, { size: 0.95, color: '#8c8e92', weight: 500 });
    }, { transparent: false, s: 30 });
    top.rotation.x = -Math.PI / 2;
    top.position.y = 4.21;
    b.root.add(top);
    return { root: b.root };
  };
}

export const buildHc595 = buildDip16('74hc595', 'SN74HC595N', 'TI  21K  ACF');
export const buildHc165 = buildDip16('74hc165', 'SN74HC165N', 'TI  22A  E4');
export const buildL293d = buildDip16('l293d', 'L293DNE', 'TI  1CM  K9');

export const buildNpn = buildTo92(['P2N', '2222A', '331']);
export const buildPnp = buildTo92(['P2N', '2907A', '216']);
